import { useState } from 'react'
import { Button, Select, Tag } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { ColumnsType } from 'antd/es/table'
import { DataTable, Loader } from '@kolab/dms-base/components/ui'
import { PageLayout } from '@kolab/dms-base/components/layout'
import { toast, getFriendlyErrorMessage } from '@kolab/dms-base/lib'
import { listCommandHistory, resendCommand } from '../../api/device.api'
import { commandKeys } from '../../api/queryKeys'
import type { CommandHistoryItem, CommandStatus } from '../../api/device.types'
import { Can, P } from '../../rbac'
import { useMonolithCursorList } from '../../hooks/useMonolithCursorList'


const STATUS_OPTIONS: { value: '' | CommandStatus; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'PENDING', label: 'PENDING' },
  { value: 'SENT', label: 'SENT' },
  { value: 'FAIL_TO_SEND', label: 'FAIL_TO_SEND' },
  { value: 'RECEIVED', label: 'RECEIVED' },
  { value: 'EXECUTED', label: 'EXECUTED' },
]

export default function CommandHistoryPage() {
  const [statusFilter, setStatusFilter] = useState<'' | CommandStatus>('')
  const queryClient = useQueryClient()

  const { items, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, refetch } =
    useMonolithCursorList<CommandHistoryItem, Awaited<ReturnType<typeof listCommandHistory>>>({
      queryKey: commandKeys.list({ status: statusFilter }),
      queryFn: ({ cursor, limit }) =>
        listCommandHistory({ cursor, limit, status: statusFilter || undefined }),
      // /v1/commands/history returns { items, nextCursor, prevCursor, hasMore }.
      resolve: (raw) => ({
        items: raw.items,
        nextCursor: raw.nextCursor,
        hasMore: raw.hasMore,
      }),
      pageSize: 30,
    })

  const resendMut = useMutation({
    mutationFn: resendCommand,
    onSuccess: () => {
      toast.success('Command resent')
      queryClient.invalidateQueries({ queryKey: commandKeys.all })
    },
    onError: (error: unknown) => {
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as { status?: number }).status
        if (status === 401 || status === 403 || (status && status >= 500)) return
      }
      toast.error(getFriendlyErrorMessage(error, 'Failed to resend command'))
    },
  })

  const columns: ColumnsType<CommandHistoryItem> = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (t: string) => {
        const color = t === 'LOCK' ? 'orange' : t === 'UNLOCK' ? 'green' : t === 'WIPE' ? 'red' : 'blue'
        return <Tag color={color}>{t}</Tag>
      },
    },
    {
      title: 'Device',
      dataIndex: 'deviceId',
      key: 'deviceId',
      render: (id: string) => <a href={`/device-ops/${id}`}>{id}</a>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => {
        const color = s === 'EXECUTED' ? 'green' : s === 'FAIL_TO_SEND' ? 'red' : s === 'PENDING' ? 'blue' : s === 'SENT' ? 'cyan' : 'default'
        return <Tag color={color}>{s}</Tag>
      },
    },
    { title: 'Created By', dataIndex: 'createdBy', key: 'createdBy' },
    { title: 'Created', dataIndex: 'createdAt', key: 'createdAt', render: (v: string) => new Date(v).toLocaleString() },
    { title: 'Received', dataIndex: 'receivedAt', key: 'receivedAt', render: (v?: string) => v ? new Date(v).toLocaleString() : '-' },
    { title: 'Executed', dataIndex: 'executedAt', key: 'executedAt', render: (v?: string) => v ? new Date(v).toLocaleString() : '-' },
    {
      title: 'Error',
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      render: (v?: string) => v ? <Tag color="red">{v}</Tag> : '-',
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        record.status === 'FAIL_TO_SEND' ? (
          <Can permission={P.device_operations.command.resend}>
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => resendMut.mutate(record.id)}
              loading={resendMut.isPending}
            >
              Resend
            </Button>
          </Can>
        ) : null
      ),
    },
  ]

  return (
    <PageLayout
      actions={<Button icon={<ReloadOutlined />} onClick={() => refetch()} />}
      filters={
        <Select
          style={{ minWidth: 180 }}
          options={STATUS_OPTIONS}
          value={statusFilter}
          onChange={(v) => setStatusFilter(v)}
        />
      }
    >
      {isLoading ? (
        <Loader />
      ) : (
        <DataTable<CommandHistoryItem>
          rowKey="id"
          columns={columns}
          dataSource={items}
          pagination={false}
          infiniteScroll={{ isFetchingNextPage, hasNextPage, fetchNextPage }}
        />
      )}
    </PageLayout>
  )
}
